(() => {
  if (customElements.get('comic-stories')) return;

  class ComicStories extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      if (document.readyState === 'loading' && !this.querySelector('[data-reader]')) {
        document.addEventListener('DOMContentLoaded', () => this.initialize(), {
          once: true,
          signal: this.controller.signal
        });
        return;
      }
      this.initialize();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.pointer = null;
      if (this.dialog?.open) this.dialog.close();
      this.unlockScroll();
      this.classList.remove('is-ready');
      this.stories?.forEach((story) => {
        story.element.hidden = false;
        story.pages.forEach((page) => { page.hidden = false; });
      });
      this.querySelectorAll('[data-controls], [data-expand], [data-hint], [data-ending]').forEach((element) => {
        element.hidden = true;
      });
    }

    initialize() {
      if (!this.isConnected || !this.controller) return;
      this.reader = this.querySelector('[data-reader]');
      this.stories = Array.from(this.querySelectorAll('[data-story]')).map((element) => ({
        element,
        key: element.dataset.story,
        title: element.dataset.title || element.querySelector('[data-story-title]')?.textContent.trim() || '',
        pages: Array.from(element.querySelectorAll('[data-page]'))
      })).filter((story) => story.key && story.pages.length);
      if (!this.reader || !this.stories.length) return;

      this.progress = this.progress || new Map();
      this.links = Array.from(this.querySelectorAll('[data-story-link]'));
      this.previous = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.position = this.querySelector('[data-position]');
      this.ending = this.querySelector('[data-ending]');
      this.nextStory = this.querySelector('[data-next-story]');
      this.shareButton = this.querySelector('[data-share]');
      this.shareStatus = this.shareButton ? this.querySelector('[data-share-status]') : null;
      this.expandButtons = this.querySelectorAll('[data-expand]');
      this.dialog = this.querySelector('[data-dialog]');
      this.dialogSurface = this.querySelector('[data-dialog-surface]');
      this.dialogTitle = this.querySelector('[data-dialog-title]');
      this.dialogImage = this.querySelector('[data-dialog-image]');
      this.dialogTranscript = this.querySelector('[data-dialog-transcript]');
      this.dialogPosition = this.querySelector('[data-dialog-position]');
      this.dialogPrevious = this.querySelector('[data-dialog-prev]');
      this.dialogNext = this.querySelector('[data-dialog-next]');
      this.original = this.querySelector('[data-original]');
      this.current = this.storyFromHash() || this.stories[0];

      this.listen(this, 'click', (event) => this.onClick(event));
      this.listen(this, 'keydown', (event) => this.onKeydown(event));
      this.listen(window, 'hashchange', () => {
        const story = this.storyFromHash();
        if (story) this.selectStory(story, { scroll: true });
      });
      this.listen(document, 'shopify:block:select', (event) => this.onBlockSelect(event));
      this.listen(document, 'pointerdown', (event) => this.onPointerDown(event), { passive: true });
      this.listen(document, 'pointermove', (event) => this.onPointerMove(event), { passive: true });
      this.listen(document, 'pointerup', (event) => this.onPointerUp(event), { passive: true });
      this.listen(document, 'pointercancel', () => { this.pointer = null; }, { passive: true });
      this.listen(this, 'dragstart', (event) => {
        if (this.swipeSurface(event.target)) event.preventDefault();
      });

      if (this.dialog) {
        this.listen(this.dialog, 'close', () => {
          this.unlockScroll();
          this.pointer = null;
          if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
          this.opener = null;
        });
        this.listen(this.dialog, 'click', (event) => {
          if (event.target !== this.dialog) return;
          const box = this.dialog.getBoundingClientRect();
          if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) {
            this.closeDialog();
          }
        });
      }
      if (this.dialogImage) {
        this.listen(this.dialogImage, 'load', () => this.updateOriginalLink());
      }
      this.querySelectorAll('[data-page] img, [data-dialog-image]').forEach((image) => {
        this.listen(image, 'load', () => this.showImageError(image, false));
        this.listen(image, 'error', () => this.showImageError(image, true));
        if ((image.getAttribute('src') || image.getAttribute('srcset')) && image.complete && !image.naturalWidth) {
          this.showImageError(image, true);
        }
      });

      this.classList.add('is-ready');
      this.querySelectorAll('[data-controls], [data-hint]').forEach((element) => { element.hidden = false; });
      this.expandButtons.forEach((button) => {
        button.hidden = !this.dialog || typeof this.dialog.showModal !== 'function';
      });
      this.render();
    }

    listen(target, name, callback, options = {}) {
      target.addEventListener(name, callback, { ...options, signal: this.controller.signal });
    }

    storyFromHash() {
      if (!window.location.hash) return this.stories[0];
      let id;
      try {
        id = decodeURIComponent(window.location.hash.slice(1));
      } catch {
        return null;
      }
      return this.stories.find((story) => story.element.id === id) || null;
    }

    get pageIndex() {
      return Math.min(this.progress.get(this.current.key) || 0, this.current.pages.length - 1);
    }

    selectStory(story, { updateHash = false, scroll = false, page } = {}) {
      if (!story) return;
      this.current = story;
      this.pointer = null;
      if (typeof page === 'number') this.progress.set(story.key, Math.max(0, Math.min(page, story.pages.length - 1)));
      if (this.shareStatus) this.shareStatus.textContent = '';
      this.render();
      if (updateHash) {
        const url = new URL(window.location.href);
        url.hash = story.element.id;
        if (url.href !== window.location.href) window.history.pushState(window.history.state, '', url.href);
      }
      if (scroll) this.focusReader();
    }

    showPage(index) {
      const nextIndex = Math.max(0, Math.min(index, this.current.pages.length - 1));
      if (nextIndex === this.pageIndex) return;
      this.progress.set(this.current.key, nextIndex);
      this.pointer = null;
      if (this.shareStatus) this.shareStatus.textContent = '';
      const focused = document.activeElement;
      this.render();
      if (focused?.disabled && this.contains(focused)) {
        const destination = this.dialog?.open ? this.querySelector('[data-close]') : this.reader;
        destination?.focus({ preventScroll: true });
      }
    }

    render() {
      const index = this.pageIndex;
      const total = this.current.pages.length;
      const label = (this.dataset.pageLabel || 'Page {page} of {total}')
        .replace('{page}', String(index + 1))
        .replace('{total}', String(total));

      this.stories.forEach((story) => {
        story.element.hidden = story !== this.current;
        story.pages.forEach((page, position) => { page.hidden = story !== this.current || position !== index; });
      });
      this.links.forEach((link) => {
        const active = link.dataset.storyLink === this.current.key;
        link.classList.toggle('is-active', active);
        if (active) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
      const title = this.current.element.querySelector('[data-story-title]');
      if (title?.id) this.reader.setAttribute('aria-labelledby', title.id);
      [this.previous, this.dialogPrevious].forEach((button) => { if (button) button.disabled = index === 0; });
      [this.next, this.dialogNext].forEach((button) => { if (button) button.disabled = index === total - 1; });
      [this.position, this.dialogPosition].forEach((output) => {
        if (!output) return;
        output.textContent = `${index + 1} / ${total}`;
        output.setAttribute('aria-label', label);
      });
      if (this.ending) this.ending.hidden = index !== total - 1;
      if (this.nextStory) {
        const next = this.stories[this.stories.indexOf(this.current) + 1];
        this.nextStory.hidden = index !== total - 1 || !next;
        if (next) this.nextStory.href = `#${next.element.id}`;
        else this.nextStory.removeAttribute('href');
      }
      [this.current.pages[index], this.current.pages[index + 1]].forEach((page) => {
        const image = page?.querySelector('img');
        if (image) image.loading = 'eager';
      });
      if (this.dialog?.open) this.renderDialog();
    }

    renderDialog() {
      const page = this.current.pages[this.pageIndex];
      const source = page.querySelector('img');
      if (!source || !this.dialogImage) return;
      if (this.dialogSurface) this.dialogSurface.scrollTop = 0;
      this.showImageError(this.dialogImage, false);
      if (this.dialogTitle) this.dialogTitle.textContent = this.current.title;
      this.dialogImage.alt = source.alt;
      if (this.dialogTranscript) {
        this.dialogTranscript.textContent = page.querySelector('[data-transcript]')?.textContent.trim() || '';
        if (this.dialogTranscript.textContent && this.dialogTranscript.id) {
          this.dialogImage.setAttribute('aria-describedby', this.dialogTranscript.id);
        } else {
          this.dialogImage.removeAttribute('aria-describedby');
        }
      }
      this.dialogImage.width = source.width || source.naturalWidth;
      this.dialogImage.height = source.height || source.naturalHeight;
      this.dialogImage.sizes = '100vw';
      if (source.srcset && !source.dataset.originalSrc) this.dialogImage.srcset = source.srcset;
      else this.dialogImage.removeAttribute('srcset');
      this.dialogImage.src = source.dataset.originalSrc || source.src;
      this.dialogImage.dataset.originalSrc = source.dataset.originalSrc || '';
      this.updateOriginalLink(source.currentSrc || source.src);
    }

    updateOriginalLink(fallback) {
      if (!this.original || !this.dialogImage) return;
      const source = this.dialogImage.dataset.originalSrc || fallback || this.dialogImage.currentSrc || this.dialogImage.src;
      if (source) this.original.href = source;
    }

    showImageError(image, visible) {
      const message = image.closest('[data-page], [data-dialog-surface]')?.querySelector('[data-image-error]');
      if (message) message.hidden = visible ? false : true;
    }

    retryImage(button) {
      const image = button.closest('[data-page], [data-dialog-surface]')?.querySelector('img');
      if (!image?.getAttribute('src')) return;
      this.showImageError(image, false);
      image.src = image.src;
    }

    onClick(event) {
      if (!(event.target instanceof Element)) return;
      const target = event.target.closest('a, button');
      if (!target || !this.contains(target) || target.disabled) return;
      if (target.matches('[data-story-link], [data-next-story]')) {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const story = target.matches('[data-next-story]')
          ? this.stories[this.stories.indexOf(this.current) + 1]
          : this.stories.find((item) => item.key === target.dataset.storyLink);
        if (!story) return;
        event.preventDefault();
        this.selectStory(story, { updateHash: true, scroll: true });
      } else if (target.matches('[data-prev], [data-dialog-prev]')) {
        this.showPage(this.pageIndex - 1);
      } else if (target.matches('[data-next], [data-dialog-next]')) {
        this.showPage(this.pageIndex + 1);
      } else if (target.matches('[data-restart]')) {
        this.showPage(0);
        this.focusReader();
      } else if (target.matches('[data-expand]')) {
        this.openDialog(target);
      } else if (target.matches('[data-close]')) {
        this.closeDialog();
      } else if (target.matches('[data-share]')) {
        this.shareStory();
      } else if (target.matches('[data-retry-image]')) {
        this.retryImage(target);
      }
    }

    onKeydown(event) {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || !(event.target instanceof Element)) return;
      if (!this.reader.contains(event.target) && !this.dialog?.contains(event.target)) return;
      if (event.target.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"]), [data-transcript]')) return;
      let index;
      if (event.key === 'ArrowLeft') index = this.pageIndex - 1;
      else if (event.key === 'ArrowRight') index = this.pageIndex + 1;
      else if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = this.current.pages.length - 1;
      else return;
      event.preventDefault();
      this.showPage(index);
    }

    swipeSurface(target) {
      if (!(target instanceof Element) || target.closest('[data-transcript], a, button')) return null;
      if (this.dialog?.open) {
        const surface = target.closest('[data-dialog-surface]');
        return surface && this.dialog.contains(surface) ? surface : null;
      }
      const image = target.closest('[data-page] img');
      const page = image?.closest('[data-page]');
      return page === this.current.pages[this.pageIndex] ? page : null;
    }

    onPointerDown(event) {
      if (!event.isPrimary || this.pointer) {
        this.pointer = null;
        return;
      }
      if (event.button !== 0 || (window.visualViewport?.scale || 1) > 1.05) return;
      const surface = this.swipeSurface(event.target);
      if (!surface) return;
      this.pointer = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        time: performance.now(),
        threshold: Math.max(44, Math.min(70, surface.clientWidth * 0.1))
      };
    }

    onPointerMove(event) {
      if (!this.pointer || event.pointerId !== this.pointer.id) return;
      const dx = Math.abs(event.clientX - this.pointer.x);
      const dy = Math.abs(event.clientY - this.pointer.y);
      if (dy > 12 && dy > dx * 1.2) this.pointer = null;
    }

    onPointerUp(event) {
      if (!this.pointer || event.pointerId !== this.pointer.id) return;
      const pointer = this.pointer;
      this.pointer = null;
      const dx = event.clientX - pointer.x;
      const dy = event.clientY - pointer.y;
      if (performance.now() - pointer.time > 1200 || (window.visualViewport?.scale || 1) > 1.05) return;
      if (Math.abs(dx) >= pointer.threshold && Math.abs(dx) > Math.abs(dy) * 1.5) {
        this.showPage(this.pageIndex + (dx < 0 ? 1 : -1));
      }
    }

    focusReader() {
      if (this.dialog?.open) this.closeDialog();
      this.reader.focus({ preventScroll: true });
      this.reader.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start'
      });
    }

    openDialog(opener) {
      if (!this.dialog || this.dialog.open || typeof this.dialog.showModal !== 'function') return;
      this.opener = opener;
      this.renderDialog();
      this.dialog.showModal();
      this.lockScroll();
      this.querySelector('[data-close]')?.focus({ preventScroll: true });
    }

    closeDialog() {
      if (this.dialog?.open) this.dialog.close();
    }

    lockScroll() {
      if (this.scrollStyles) return;
      this.scrollStyles = [document.documentElement, document.body].map((element) => ({
        element,
        value: element.style.getPropertyValue('overflow'),
        priority: element.style.getPropertyPriority('overflow')
      }));
      this.scrollStyles.forEach(({ element }) => element.style.setProperty('overflow', 'hidden'));
    }

    unlockScroll() {
      this.scrollStyles?.forEach(({ element, value, priority }) => {
        if (value) element.style.setProperty('overflow', value, priority);
        else element.style.removeProperty('overflow');
      });
      this.scrollStyles = null;
    }

    async shareStory() {
      if (this.shareBusy) return;
      this.shareBusy = true;
      if (this.shareButton) this.shareButton.disabled = true;
      if (this.shareStatus) this.shareStatus.textContent = '';
      const story = this.current;
      const signal = this.controller.signal;
      const url = new URL(window.location.href);
      url.hash = story.element.id;
      try {
        if (typeof navigator.share === 'function') {
          await navigator.share({ title: story.title, url: url.href });
        } else {
          if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
          await navigator.clipboard.writeText(url.href);
          if (!signal.aborted && this.current === story && this.shareStatus) {
            this.shareStatus.textContent = this.dataset.shareSuccess || 'Story link copied.';
          }
        }
      } catch (error) {
        if (error.name !== 'AbortError' && !signal.aborted && this.current === story && this.shareStatus) {
          this.shareStatus.textContent = this.dataset.shareError || 'Unable to share. Copy the page address instead.';
        }
      } finally {
        this.shareBusy = false;
        if (this.shareButton) this.shareButton.disabled = false;
      }
    }

    onBlockSelect(event) {
      if (!(event.target instanceof Element) || !this.contains(event.target)) return;
      const element = event.target.closest('[data-story]');
      const story = this.stories.find((item) => item.element === element);
      if (!story) return;
      const page = event.target.closest('[data-page]');
      this.selectStory(story, {
        scroll: true,
        page: page ? story.pages.indexOf(page) : undefined
      });
    }
  }

  customElements.define('comic-stories', ComicStories);
})();
